import type { Appointment } from "@/features/clinic/types/appointment";
import type {
  ClinicInvoice,
  DuplicateInvoiceConflict,
  InvoiceStatus,
} from "@/features/clinic/types/invoice";

import { HTTPError } from "ky";

import { APPOINTMENT_STATUS } from "@/features/clinic/types/appointment";
import {
  INVOICE_ERROR_CODE,
  INVOICE_REVIEW_STATE,
  INVOICE_STATUS,
} from "@/features/clinic/types/invoice";

export const INVOICE_STATUS_LABEL: Record<InvoiceStatus, string> = {
  [INVOICE_STATUS.DRAFT]: "Draft",
  [INVOICE_STATUS.UNPAID]: "Not yet paid",
  [INVOICE_STATUS.PAID]: "Paid",
  [INVOICE_STATUS.VOID]: "Void",
};

/**
 * A real bill: not voided, not held as a suspected duplicate. Paid or not -
 * this is what the visit was billed, which is what says the patient came in
 * and what the contact's Billing tab sums to say what is outstanding.
 */
export const isBilledInvoice = (invoice: ClinicInvoice) =>
  invoice.status !== INVOICE_STATUS.VOID &&
  invoice.reviewState !== INVOICE_REVIEW_STATE.HELD;

/**
 * Mirrors the backend's `countingInvoiceConditions`: billed, and not still
 * unpaid - money billed and not yet taken is not revenue. The list has to add
 * up to the appointment's own `value`, so the exclusions must match the
 * server's exactly.
 */
export const countsTowardValue = (invoice: ClinicInvoice) =>
  isBilledInvoice(invoice) && invoice.status !== INVOICE_STATUS.UNPAID;

export const sumInvoiceAmounts = (invoices: ClinicInvoice[]) =>
  invoices
    .filter(isBilledInvoice)
    .reduce((sum, invoice) => sum + Number(invoice.total), 0);

/** Whether the server has priced the visit - from receipts, advances or invoices. */
export const isAppointmentPriced = (appointment: Pick<Appointment, "value">) =>
  appointment.value != null;

export type InvoiceEmptyState = {
  /** The visit happened and nothing has priced it - the one state worth flagging. */
  isMissing: boolean;
  title: string;
  description: string;
};

/**
 * What the invoice list says when it has no rows. The value is the server's
 * to derive, and cleared receipts or advances attached to the visit price it
 * without any invoice - so an empty list on a priced visit is not a gap, and
 * must not ask the front desk to record money a second time.
 */
export const invoiceEmptyState = (
  appointment: Pick<Appointment, "status" | "value">,
  crmName: string | null = null,
): InvoiceEmptyState => {
  if (isAppointmentPriced(appointment)) {
    return {
      isMissing: false,
      title: "Settled without an invoice",
      description: crmName
        ? `Receipts or advances from ${crmName} set its value.`
        : "Receipts or advances attached to this visit set its value. Record an invoice only if one was issued.",
    };
  }

  const isMissing = appointment.status === APPOINTMENT_STATUS.COMPLETED;

  // With a CRM the money is recorded there: the gap is closed in the CRM and
  // pulled here, never typed on this side.
  if (crmName) {
    return {
      isMissing,
      title: isMissing
        ? `The visit is done, ${crmName} has nothing for it`
        : `Nothing from ${crmName} yet`,
      description: `Record the payment in ${crmName}, then refresh here.`,
    };
  }

  if (isMissing) {
    return {
      isMissing: true,
      title: "The visit is done, the value isn't",
      description:
        "Record what they paid so this appointment counts toward revenue.",
    };
  }

  return {
    isMissing: false,
    title: "No invoices yet",
    description: "The first invoice sets the appointment value.",
  };
};

type DuplicateInvoiceBody = {
  code: string;
  message: string;
  duplicates: ClinicInvoice[];
  addAnywayAllowed?: boolean;
};

const isDuplicateBody = (body: unknown): body is DuplicateInvoiceBody =>
  !!body &&
  typeof body === "object" &&
  (body as DuplicateInvoiceBody).code ===
    INVOICE_ERROR_CODE.DUPLICATE_INVOICE &&
  Array.isArray((body as DuplicateInvoiceBody).duplicates);

/**
 * Reads the 409 body a duplicate raises. Returns null for every other failure,
 * including a 409 from somewhere else, so the caller rethrows it untouched.
 *
 * The response is cloned rather than read: the api client's own error hook
 * consumes the body afterwards, and a stream can only be read once.
 */
export const toDuplicateConflict = async (
  error: unknown,
): Promise<DuplicateInvoiceConflict | null> => {
  if (!(error instanceof HTTPError) || error.response.status !== 409) {
    return null;
  }

  try {
    const body: unknown = await error.response.clone().json();

    if (!isDuplicateBody(body)) return null;

    return {
      code: body.code,
      message: body.message,
      duplicates: body.duplicates,
      // A server from before the flag existed still accepts the override, so
      // absent has to read as allowed - defaulting to false would strand
      // whoever is typing with no way forward.
      addAnywayAllowed: body.addAnywayAllowed ?? true,
    };
  } catch {
    return null;
  }
};
